import cn from 'clsx'
import Pane from 'components/Common/Pane'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import OfferPageGallery from '../OfferPageGallery'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { clearBookingCalendar, fetchCalendars } from 'actions/CalendarActions'
import {
  fetchAvailableRatesForOffer,
  fetchOfferFlightPrice,
  fetchRelatedTravelItems,
} from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { replace } from 'connected-react-router'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { urlHashDatePicker } from 'constants/url'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import { generateRoomOccupants, isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import { encodeSearchParams } from 'lib/search/searchUtils'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import OfferAirportSelect from '../Filters/Airport/OfferAirportSelect'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferDatePickerSelect from '../Filters/DatePicker/OfferDatePickerSelect'
import OfferDatePickerSelectBox from '../Filters/DatePicker/OfferDatePickerSelectBox'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import AlwaysOnSidebarFilters from '../AlwaysOn/AlwaysOnSidebarFilters'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import OfferYmalStickyFooter from 'components/Recommendations/YouMayAlsoLike/Offer/OfferYmalStickyFooter'
import { InViewHookResponse, useInView } from 'react-intersection-observer'
import OfferPageFlightDealBanner from '../OfferPageFlightDealBanner'
import ModalProvider from 'contexts/ModalProvider'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import SavePromptHotel from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptHotel'
import useShareModalConfig from 'hooks/useShareModalConfig'
import moment from 'moment'
import PropertyRoomOptions from './PropertyRoomOptions'
import PropertyStickyFooter from './PropertyStickyFooter'
import PropertyIntro from './PropertyIntro'
import PropertyStickyHeader from './PropertyStickyHeader'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import PropertyRoomOptionsMobile from './MobileBookingFlow/PropertyRoomOptionsMobile'
import AccommodationOfferPageStickyFooter from '../Accommodation/AccommodationOfferPageStickyFooter'
import SinglePropertyHotelFlightsToggleButton from '../Accommodation/HotelFlightsToggle/SinglePropertyHotelFlightsToggleButton'
import AccommodationOfferPageStickyHeaderMobile from '../Accommodation/AccommodationOfferPageStickyHeaderMobile'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { getCheapestSelectedPackageOptionWithDefault, getCheckoutDate, isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { useLocation } from 'react-router'
import GeoContext from 'contexts/geoContext'

const RelativeBlock = styled.form`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.no-padding {
    padding: 0;
  }
`

const FlexGroup = styled(Group)`
  flex-grow: 1;
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;
`

interface Props {
  shouldUseLereStickyFooter: boolean;
  lereYmalStickyFooterShowTrigger: InViewHookResponse;
  offer: App.HotelOffer;
  airport?: App.Airport;
  duration: number;
  activeFilter?: OfferPageFilterType;
  rooms?: Array<App.RoomOccupants>;
  checkInDate?: string;
  checkOutDate?: string;
  flights?: boolean;
  selectedPackage?: App.Package;
  availableForPurchase?: boolean;
  bestPackage: OfferPackageOptionView;
  room?: App.PackageRoomType;
}

const fallbackRooms = [generateRoomOccupants()]

const currentTimer = {} as Record<string, any>

const slideTimer = (name: string, callback: any, ms: number) => {
  if (currentTimer[name]) {
    clearTimeout(currentTimer[name])
  }
  currentTimer[name] = setTimeout(callback, ms)
}

function PropertyContent(props: Props) {
  const {
    shouldUseLereStickyFooter,
    lereYmalStickyFooterShowTrigger,
    offer,
    airport,
    duration,
    activeFilter,
    rooms = fallbackRooms,
    selectedPackage,
    checkInDate,
    checkOutDate,
    flights,
    availableForPurchase,
    bestPackage,
  } = props

  const location = useLocation()
  const query = useQsQueryParams<{ roomRateId?: string, roomTypeId?: string}>()
  const { originAirports, airports } = useContext(GeoContext)
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)

  const [viewedDates, setViewedDates] = useState(!!checkInDate)

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()

  const { pricing, pricingWithFlights } = bestPackage
  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showDiscountPill = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent && !offer.bundledWithFlightsOnly) || (offer.bundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  const loggedOutSavedPercent = showDiscountPill ? offer.bundledWithFlightsOnly ? pricingWithFlights?.discountPercent : pricing.discountPercent : undefined

  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.Occupants>>([])
  const lastFetchedAirport = useRef<App.Airport | null>(null)
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const isMobileDevice = useScreenSizeOnly('mobile')
  const isNewMobileBookingFlowEnabled = useOptimizelyExperiment(OptimizelyExperiments.croAlwaysOnNewMobileBooking, isMobileDevice)
  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowEnabled

  const shouldShowCalendarWithoutPricing = useOptimizelyExperiment(OptimizelyExperiments.accommOfferCalendarWithoutPricing)

  const showHotelAndFlightsToggle = !isMobileDevice && !offer.bundledWithFlightsOnly && offer.offerFlightsEnabled && config.businessTraveller.currentAccountMode !== 'business'

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
    }).toString(), [checkInDate, checkOutDate, rooms, location.search])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
    previousRooms !== rooms ||
    previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  useIdleTimeReporter(IdleTimeSource.AlwaysOn)

  const fetchCalendarData = useCallback(() => {
    if (!offer.packages.length || !isValidOccupancy(...rooms)) {
      // if we have any invalid ages or no packages, don't try fetch - it'll fail anyway
      return
    }

    dispatch(fetchCalendars(offer.id, {
      occupants: rooms,
      flightOrigin: airport?.code,
      packages: [selectedPackage as App.HotelPackage ?? offer.lowestPricePackage!],
    }))
    dispatch(fetchCalendars(offer.id, {
      occupants: rooms,
      flightOrigin: airport?.code,
      packages: offer.packages.filter(p => p.duration === duration),
    }))

    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport ?? null
  }, [airport, dispatch, duration, offer, rooms, selectedPackage])

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const hasRoomsChanged = useMemo(() => {
    const currentRooms = rooms ?? fallbackRooms
    const currentOccupancyNumber = currentRooms.map(generateOccupancyStringByRoom).join('|')
    const lastOccupancyNumber = (lastFetchedRooms.current ?? fallbackRooms).map(generateOccupancyStringByRoom).join('|')
    return currentOccupancyNumber !== lastOccupancyNumber
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, activeFilter])

  useEffect(() => {
    const hasRooms = rooms !== fallbackRooms
    if (previousFilter === 'capacity' && !hasRooms) {
      if (!activeFilter) {
        autoScrollToFilters()
        scrollAfterFilterClose.current = false
      }
    }

    if (activeFilter === 'checkin' && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else if (viewedDates && lastFetchedAirport.current !== airport) {
      // Filter has just closed, so we're applying it now
      // rooms has changed, all loaded calendars are no longer valid as we load calendars for specific
      // rooms (availablity), so clear them and refetch
      dispatch(clearBookingCalendar())
      fetchCalendarData()
    } else if (hasRoomsChanged) {
      dispatch(clearBookingCalendar())
      slideTimer('fetchCalendarData', fetchCalendarData, 1000)
    }

    if (activeFilter) {
      autoScrollToFilters()
    }
  }, [activeFilter, previousFilter, hasRoomsChanged, fetchCalendarData]) // eslint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useEffect(() => {
    if (query.roomTypeId && query.roomRateId) {
      const roomOption = { roomTypeId: query.roomTypeId, roomRateId: query.roomRateId }
      stateDispatch({ type: OfferPageActions.setRoomOption, roomOption })
    }
  }, [stateDispatch, query.roomTypeId, query.roomRateId])

  const hasSelectedPackageWithDifferentDuration = selectedPackage && selectedPackage?.duration !== duration
  useEffect(() => {
    const useDynamicCancellationPolicies = offer.property.useDynamicCancellationPolicies
    const occupanciesInvalid = rooms.some(item => item.childrenAge?.some(age => age < 0))
    if (checkInDate && checkOutDate && !occupanciesInvalid && (!activeFilter || hasSelectedPackageWithDifferentDuration) && useDynamicCancellationPolicies) {
      dispatch(fetchAvailableRatesForOffer(
        offer,
        {
          occupants: rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          flightOrigin: airport?.code,
        },
      ))
    }
  }, [duration, activeFilter, checkInDate, offer, hasSelectedPackageWithDifferentDuration, rooms, airport, checkOutDate, dispatch])

  useEffect(() => {
    slideTimer('fetchRelatedTravelItems', () => {
      dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
    }, 2000)
  }, [offer, duration, checkInDate, dispatch, rooms, checkOutDate])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    if (airport) {
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(offer.id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate.capacities)
  }, [offer.packages])

  const isValidRoomOccupancy = offer.bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = destinationAirport &&
  (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) &&
  isValidRoomOccupancy &&
  config.businessTraveller.currentAccountMode !== 'business'

  // ------------- LERE sticky footer -------------------
  const recommendationFilters = useMemo(() => ({
    checkIn: checkInDate,
    checkOut: checkInDate ? moment(checkInDate).clone().add(duration, 'days').format(ISO_DATE_FORMAT) : undefined,
    rooms,
  }), [checkInDate, duration, rooms])

  const lereYmalStickyFooterHideTrigger = useInView({ triggerOnce: false })

  const canShowYmalFooter = lereYmalStickyFooterShowTrigger.inView && !lereYmalStickyFooterHideTrigger.inView
  const showDealBanner = airport && destinationAirport

  // ------------- END of LERE sticky footer -------------------

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  const shareConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', '),
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  return <>
    <ImmersiveSaveButtonPortal>
      <BookmarkButton offer={offer} />
    </ImmersiveSaveButtonPortal>
    <TripGuard>
      <SavePromptHotel offer={offer} />
    </TripGuard>
    <RelativeBlock onSubmit={(e) => e.preventDefault()}>
      {useNewMobileBookingFlow && <AccommodationOfferPageStickyHeaderMobile />}
      <div>
        <Backdrop visible={!!activeFilter} data-testid="filter-backdrop" />
        <CSSBreakpoint min="tablet">
          <PropertyStickyHeader
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />
        </CSSBreakpoint>
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations hideOffers={!!activeFilter} />
          </div>
        )}
        {availableForPurchase && (
          <WalledContent enforced={offer.walledGarden}>
            <Breakpoint max="mobile">
              <ModalProvider>
                <AlwaysOnSidebarFilters
                  onViewPackageOptions={scrollToPackageOptions}
                  capacities={occupancyLimits}
                  shouldShowCalendarWithoutPricing={shouldShowCalendarWithoutPricing}
                />
              </ModalProvider>
            </Breakpoint>
          </WalledContent>
        )}
        <span ref={lereYmalStickyFooterHideTrigger.ref} />
        <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
        <Pane>
          <PropertyIntro
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            shareConfig={shareConfig}
          />
        </Pane>
      </div>
      <ScrollAnchor ref={packageOptionsAnchor} />
      <SoftOfferPageWalledGarden>
        <span ref={lereYmalStickyFooterShowTrigger.ref} />
        <PackageOptionsPane id="Package-Options">
          <StyledLayoutContainer className={cn({ 'no-padding': useNewMobileBookingFlow })}>
            <VerticalSpacer gap={32}>
              {availableForPurchase && (
                <WalledContent enforced={offer.walledGarden}>
                  <>
                    {!useNewMobileBookingFlow && (
                      <CSSBreakpoint max="mobile">
                        <Group direction="vertical" gap={8}>
                          <OfferCapacityWithAgesSelectBox />
                          {shouldShowCalendarWithoutPricing && <OfferDatePickerSelectBox />}
                          {!shouldShowCalendarWithoutPricing && <OfferCalendarDatePickerSelectBox />}
                          {showFlightInformation && <OfferAirportSelectBox />}
                        </Group>
                      </CSSBreakpoint>
                    )}
                    <CSSBreakpoint min="tablet">
                      <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
                      {showHotelAndFlightsToggle && (
                        <Group ref={desktopFiltersWrapperRef} fullWidth direction="horizontal" gap={8} horizontalAlign="space-between" wrap="wrap">
                          <FlexGroup direction="horizontal" horizontalAlign="center" desktopHorizontalAlign="start">
                            <SinglePropertyHotelFlightsToggleButton />
                          </FlexGroup>
                          <FlexDesktopFilterBlock direction="horizontal" gap={8} horizontalAlign="center" desktopHorizontalAlign="end" wrap="wrap" className={cn({ open: !!activeFilter })}>
                            {showFlightInformation && flights && <OfferAirportSelect />}
                            <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                            {shouldShowCalendarWithoutPricing && <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />}
                            {!shouldShowCalendarWithoutPricing && <OfferCalendarDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />}
                          </FlexDesktopFilterBlock>
                        </Group>
                      )}
                      {!showHotelAndFlightsToggle && (
                        <DesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" className={cn({ open: !!activeFilter })}>
                          <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                          {shouldShowCalendarWithoutPricing && <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />}
                          {!shouldShowCalendarWithoutPricing && <OfferCalendarDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />}
                          {showFlightInformation && <OfferAirportSelect />}
                        </DesktopFilterBlock>
                      )}
                    </CSSBreakpoint>
                  </>
                </WalledContent>
              )}

              {!useNewMobileBookingFlow && !shouldShowCalendarWithoutPricing && (
                <VerticalSpacer gap={8}>
                  <Subtitle variant="subtitle3" align="center">
                    Select duration
                  </Subtitle>
                  <OfferDurationTabs />
                </VerticalSpacer>
              )}

              <LayoutContainer>
                {showDealBanner && config.FLIGHT_ENABLED && <OfferPageFlightDealBanner
                  destinationAirportCode={destinationAirport.code}
                  originAirportCode={airport.code}
                />}
              </LayoutContainer>
              {offer.packages.length > 0 && !useNewMobileBookingFlow && <PropertyRoomOptions
                showFlightInformation={showFlightInformation}
                destinationAirport={destinationAirport}
                onViewPackageOptions={scrollToPackageOptions}
              />}
              {offer.packages.length > 0 && useNewMobileBookingFlow && <PropertyRoomOptionsMobile
                showFlightInformation={showFlightInformation}
              />}
            </VerticalSpacer>
          </StyledLayoutContainer>
        </PackageOptionsPane>
        {!useNewMobileBookingFlow && availableForPurchase && (!shouldUseLereStickyFooter || !canShowYmalFooter) &&
          <PropertyStickyFooter
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />
          }
        {useNewMobileBookingFlow && availableForPurchase && <AccommodationOfferPageStickyFooter />}
      </SoftOfferPageWalledGarden>
    </RelativeBlock>
    <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    {shouldUseLereStickyFooter && !useNewMobileBookingFlow && (
      <ErrorBoundary fallback={null}>
        <OfferYmalStickyFooter
          seedOffer={offer}
          filters={recommendationFilters}
          show={canShowYmalFooter}
          initialShowDelayMs={2000}
          hideImmediately={!!activeFilter}
          pageSource="AlwaysOn"
        />
      </ErrorBoundary>
    )}
  </>
}

type MappedProps = Pick<Props,
'airport' |
'duration' |
'activeFilter' |
'rooms' |
'selectedPackage' |
'checkInDate' |
'checkOutDate' |
'flights' |
'availableForPurchase' |
'bestPackage' |
'room'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  selectedPackage: state.package,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  flights: state.flights,
  room: state.room,
  availableForPurchase: isAvailableForPurchase(state, appState),
  /** For AB test "cro_sticky_walled_garden" remove afterwards */
  bestPackage: getCheapestSelectedPackageOptionWithDefault(appState, state),
}))(PropertyContent)
